"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { toggleWishlist } from "@/app/actions/wishlist";
import { captureAnalyticsEvent } from "@/components/analytics/analytics-provider";
import { useToast } from "@/components/ui/toast-provider";
import { getWishlistCopy } from "@/content/wishlist-copy";
import { Locale } from "@/i18n-config";
import {
  WISHLIST_AUTH_REQUIRED_ERROR,
  WISHLIST_STORAGE_UNAVAILABLE_ERROR,
} from "@/lib/wishlist/constants";
import { normalizeWishlistCourseSlug, normalizeWishlistSlugs, setWishlistMembership } from "@/lib/wishlist/state";

type UseWishlistOptions = {
  lang: Locale;
  isAuthenticated: boolean;
  initialSlugs?: readonly string[];
};

type ToggleWishlistResult = {
  wished: boolean;
};

function isAuthRequiredError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  return error.message.includes(WISHLIST_AUTH_REQUIRED_ERROR);
}

function isWishlistStorageUnavailableError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  return error.message.includes(WISHLIST_STORAGE_UNAVAILABLE_ERROR);
}

function withUpdatedSlugSet(
  slugs: ReadonlySet<string>,
  courseSlug: string,
  isEnabled: boolean
): Set<string> {
  return setWishlistMembership(slugs, courseSlug, isEnabled);
}

export function useWishlist({ lang, isAuthenticated, initialSlugs = [] }: UseWishlistOptions) {
  const toast = useToast();
  const copy = getWishlistCopy(lang);

  const normalizedInitialSlugs = useMemo(
    () => normalizeWishlistSlugs(initialSlugs),
    [initialSlugs.join("|")]
  );

  const [wishedSlugsSet, setWishedSlugsSet] = useState<Set<string>>(
    () => new Set(normalizedInitialSlugs)
  );
  const [pendingSlugs, setPendingSlugs] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setWishedSlugsSet(new Set(normalizedInitialSlugs));
  }, [normalizedInitialSlugs]);

  const isWished = useCallback(
    (courseSlug: string) => {
      const normalizedSlug = normalizeWishlistCourseSlug(courseSlug);
      return normalizedSlug.length > 0 && wishedSlugsSet.has(normalizedSlug);
    },
    [wishedSlugsSet]
  );

  const isPending = useCallback(
    (courseSlug: string) => {
      const normalizedSlug = normalizeWishlistCourseSlug(courseSlug);
      return normalizedSlug.length > 0 && pendingSlugs.has(normalizedSlug);
    },
    [pendingSlugs]
  );

  const toggle = useCallback(
    async (courseSlug: string): Promise<ToggleWishlistResult> => {
      const normalizedSlug = normalizeWishlistCourseSlug(courseSlug);
      if (!normalizedSlug) {
        return { wished: false };
      }

      if (!isAuthenticated) {
        toast.showToast(copy.loginToSave);
        return { wished: false };
      }

      if (pendingSlugs.has(normalizedSlug)) {
        return { wished: wishedSlugsSet.has(normalizedSlug) };
      }

      const wasWished = wishedSlugsSet.has(normalizedSlug);
      const optimisticWished = !wasWished;

      setWishedSlugsSet((previous) =>
        withUpdatedSlugSet(previous, normalizedSlug, optimisticWished)
      );
      setPendingSlugs((previous) => {
        const next = new Set(previous);
        next.add(normalizedSlug);
        return next;
      });

      try {
        const result = await toggleWishlist(normalizedSlug);

        if (result.unavailable) {
          setWishedSlugsSet((previous) =>
            withUpdatedSlugSet(previous, normalizedSlug, wasWished)
          );
          toast.showToast(copy.unavailable);
          return { wished: wasWished };
        }

        setWishedSlugsSet((previous) =>
          withUpdatedSlugSet(previous, normalizedSlug, result.wished)
        );

        toast.showToast(result.wished ? copy.added : copy.removed);
        captureAnalyticsEvent(result.wished ? "wishlist_added" : "wishlist_removed", {
          course_slug: normalizedSlug,
        });
        return { wished: result.wished };
      } catch (error) {
        setWishedSlugsSet((previous) =>
          withUpdatedSlugSet(previous, normalizedSlug, wasWished)
        );

        if (isAuthRequiredError(error)) {
          toast.showToast(copy.loginToSave);
        } else if (isWishlistStorageUnavailableError(error)) {
          toast.showToast(copy.unavailable);
        }

        return { wished: wasWished };
      } finally {
        setPendingSlugs((previous) => {
          const next = new Set(previous);
          next.delete(normalizedSlug);
          return next;
        });
      }
    },
    [
      copy.added,
      copy.loginToSave,
      copy.removed,
      copy.unavailable,
      isAuthenticated,
      pendingSlugs,
      toast,
      wishedSlugsSet,
    ]
  );

  const wishedSlugs = useMemo(() => [...wishedSlugsSet], [wishedSlugsSet]);

  return {
    wishedSlugs,
    isWished,
    isPending,
    toggle,
  };
}
